import api from '../api';

export const PROMOTION_PLACEMENTS = ['home_lower', 'news_top', 'article_bottom'] as const;
export type PromotionPlacement = typeof PROMOTION_PLACEMENTS[number];
export type PromotionStatus = 'draft' | 'published' | 'paused' | 'archived';
export type PromotionEffectiveStatus = 'draft' | 'scheduled' | 'active' | 'paused' | 'ended' | 'archived';
export type PromotionAssetKind = 'desktop' | 'mobile';
export type PromotionEventKind = 'impression' | 'click';
export interface PromotionCreative { desktop_asset_id: string | null; mobile_asset_id: string | null; alt: string; }
export interface PromotionInput {
  name: string; advertiser: string; target_url: string; placements: PromotionPlacement[];
  status: PromotionStatus; starts_at: string; ends_at: string; priority: number;
  creatives: { ru: PromotionCreative; kk: PromotionCreative };
}
export interface PromotionCampaign extends PromotionInput {
  id: string; revision: number; effective_status: PromotionEffectiveStatus;
  created_at: string; updated_at: string;
}
export interface PromotionAsset { id: string; url: string; kind: PromotionAssetKind; width: number; height: number; bytes: number; }
export interface PublicPromotion {
  id: string; revision: number; advertiser: string; target_url: string; placement: PromotionPlacement;
  desktop_url: string; mobile_url: string; alt_desktop: string; alt_mobile: string;
  ends_at: string; metrics_token: string | null;
}
export interface PromotionMetrics { impressions: number; clicks: number; ctr: number; }
export interface PromotionStats {
  totals: PromotionMetrics; by_day: Array<PromotionMetrics & { date: string }>;
  by_placement: Array<PromotionMetrics & { placement: PromotionPlacement }>;
  date_from: string; date_to: string; retention_days: number;
}
export const getActivePromotion = async (placement: PromotionPlacement, lang: 'ru' | 'kk', campaignId?: string, signal?: AbortSignal): Promise<{ item: PublicPromotion | null }> =>
  (await api.get('/promotions/active', { params: { placement, lang, campaign_id: campaignId }, signal, timeout: 3000 })).data;
export const recordPromotionEvent = async (token: string, kind: PromotionEventKind): Promise<void> => {
  await api.post('/promotions/events', { token, kind }, { timeout: 2000 });
};
export const listPromotions = async (params: { q?: string; status?: string; placement?: PromotionPlacement; skip: number; limit: number }, signal?: AbortSignal): Promise<{ items: PromotionCampaign[]; total: number }> =>
  (await api.get('/admin/promotions', { params, signal, timeout: 15000 })).data;
export const getPromotion = async (id: string, signal?: AbortSignal): Promise<PromotionCampaign> =>
  (await api.get(`/admin/promotions/${encodeURIComponent(id)}`, { signal, timeout: 15000 })).data;
export const savePromotion = async (input: PromotionInput, existing?: { id: string; revision: number }): Promise<PromotionCampaign> => existing
  ? (await api.put(`/admin/promotions/${encodeURIComponent(existing.id)}`, { ...input, revision: existing.revision }, { timeout: 15000 })).data
  : (await api.post('/admin/promotions', input, { timeout: 15000 })).data;
export const uploadPromotionAsset = async (file: File, kind: PromotionAssetKind): Promise<PromotionAsset> => {
  const body = new FormData(); body.append('file', file); body.append('kind', kind);
  return (await api.post('/admin/promotions/assets', body, { timeout: 60000 })).data;
};
export const getPromotionAsset = async (id: string, signal?: AbortSignal): Promise<Blob> =>
  (await api.get(`/admin/promotions/assets/${encodeURIComponent(id)}`, { responseType: 'blob', signal, timeout: 15000 })).data;
export const getPromotionStats = async (id: string, dateFrom: string, dateTo: string, signal?: AbortSignal): Promise<PromotionStats> =>
  (await api.get(`/admin/promotions/${encodeURIComponent(id)}/stats`, { params: { date_from: dateFrom, date_to: dateTo }, signal, timeout: 15000 })).data;
